import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  Chip,
  CircularProgress,
  type AutocompleteRenderInputParams,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useTranslation } from "react-i18next";
import { AppTextField } from "../form";
import { APP_FORM_DEFAULTS } from "../form/constants";
import { searchChatIndices } from "../../services/chatService";
import { widgetService } from "../../services/widgetService";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

export type OpenSearchIndexPickerProps = {
  value: string[];
  onChange: (indices: string[]) => void;
  label?: string;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  /** When false, API search is paused (e.g. closed popover). Default true. */
  searchEnabled?: boolean;
  /** When true, only indices allowed for widget queries are searchable. Default false (chat). */
  policyFiltered?: boolean;
};

export default function OpenSearchIndexPicker({
  value,
  onChange,
  label,
  helperText,
  placeholder,
  disabled,
  size,
  fullWidth,
  searchEnabled = true,
  policyFiltered = false,
}: OpenSearchIndexPickerProps) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const pickerStyles = custom.components.openSearchIndexPicker;
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [listboxOpen, setListboxOpen] = useState(false);

  const canSearch = inputValue.trim().length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!searchEnabled) {
      setOptions([]);
      setError("");
      setLoading(false);
      return;
    }

    const q = inputValue.trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setError("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    const timer = window.setTimeout(() => {
      const search = policyFiltered ? widgetService.searchIndices(q) : searchChatIndices(q);
      search
        .then((list) => setOptions(list))
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
          setOptions([]);
        })
        .finally(() => setLoading(false));
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [inputValue, searchEnabled, policyFiltered]);

  useEffect(() => {
    if (canSearch && (loading || options.length > 0 || Boolean(error))) {
      setListboxOpen(true);
    }
  }, [canSearch, loading, options.length, error]);

  const mergedOptions = useMemo(() => {
    const set = new Set<string>([...value, ...options]);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [value, options]);

  const noOptionsText = useMemo(() => {
    if (loading) return t("chat.indexPicker.loading");
    if (!canSearch) return t("chat.indexPicker.typeToSearch");
    if (error) return error;
    return t("chat.indexPicker.noMatch");
  }, [loading, canSearch, error, t]);

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <AppTextField
      {...params}
      label={label ?? t("widget.indices")}
      helperText={helperText}
      placeholder={placeholder ?? t("chat.indexPicker.placeholder")}
      margin={APP_FORM_DEFAULTS.margin}
      size={size ?? APP_FORM_DEFAULTS.size}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {loading ? <CircularProgress color="inherit" size={18} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      open={listboxOpen}
      onOpen={() => setListboxOpen(true)}
      onClose={() => setListboxOpen(false)}
      openOnFocus={false}
      filterOptions={(x) => x}
      loading={loading}
      disabled={disabled}
      options={canSearch ? mergedOptions : value}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        setInputValue(next);
        if (reason === "input") {
          setListboxOpen(next.trim().length >= MIN_SEARCH_LENGTH);
        }
        if (reason === "clear" || reason === "reset") {
          setListboxOpen(false);
        }
      }}
      onChange={(_, next) => onChange(next)}
      noOptionsText={noOptionsText}
      size={size ?? APP_FORM_DEFAULTS.size}
      fullWidth={fullWidth ?? APP_FORM_DEFAULTS.fullWidth}
      slotProps={{
        popper: {
          sx: pickerStyles.popper,
        },
        paper: {
          sx: pickerStyles.paper,
        },
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...chipProps } = getTagProps({ index });
          return <Chip key={key} {...chipProps} label={option} size="small" />;
        })
      }
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option}>
          <Checkbox icon={icon} checkedIcon={checkedIcon} sx={pickerStyles.checkbox} checked={selected} />
          {option}
        </li>
      )}
      renderInput={renderInput}
    />
  );
}
